// ============================================================================
// Message rendering — addEl, sysMsg, tool cards, context meter, permissions
// ============================================================================
// Extracted from main.js. Handles all DOM rendering for chat messages,
// tool cards, plan cards, permission prompts, the context-window meter,
// and the paginated history replay loader.
//
// Depends on: $, inner, messagesEl, sendBtn, typingIndicator (dom.js),
//   renderMarkdown (markdown.js), wireFileLinks (file-viewer.js),
//   haptic (shared.js global), and several functions/state from main.js
//   passed via initMessageRendering(deps).

import { $, inner, messagesEl, sendBtn, typingIndicator } from "./dom.js";
import { renderMarkdown } from "./markdown.js";
import { wireFileLinks } from "./file-viewer.js";
import { renderMetaWidgets } from "./widgets.js";
import { addCopyButtons } from "./copy.js";
import { addPinButtons } from "./pins.js";
import { icon as svgIcon, fileIcon as svgFileIcon } from "./icons.js";

// ---- deps (set by initMessageRendering) ----
let scrollDown = null;
let trimDomMessages = null;
let debouncedCacheMessages = null;
let sendFn = null;
let updateModeIndicator = null;
let agentMsgs = null;
let toolCards = null;
let getReplayingHistory = null;
let getReplayTotal = null;
let getReplayReceived = null;
let getReplayLoaderEl = null;
let setReplayLoaderEl = null;
let getLoadingHistory = null;
let setLoadingHistory = null;
let getActiveCwd = null;
let setAvailableCommands = null;
let setCurrentModeId = null;

// ---- shared render state (accessible from main.js) ----
// These variables are set from main.js during paginated history replay
// and scroll-back, and read by addEl/handleUpdate in this module.
export const renderState = {
  // When set, addEl inserts new elements before this node instead of
  // appending. Used by scroll-back (and initial replay older pages) to
  // prepend older history at the top.
  addElInsertBefore: null,
  // Optional target container (e.g. DocumentFragment for atomic swap)
  addElTarget: null,
  replaySwapPending: false,
  // When true, addEl records each inserted top-level node into `pageNodes`
  // so the page handler can tag it with a cursor and cache it per-node.
  renderingPage: false,
  pageNodes: [],
  // Prepend batch state (scroll-back / older replay pages): chunks for an
  // agent bubble that ALREADY exists carry text that is *earlier* than
  // what's rendered — it must be prefixed, not appended, or the message
  // reads tail-then-head. Accumulated per mid during the batch, flushed by
  // flushPrependBuffers(). prependNewMids tracks bubbles created inside
  // the batch — their chunks append normally within the batch.
  prependBuf: new Map(),
  prependNewMids: new Set(),
  // Same idea for tool cards: cards created inside the batch still take
  // normal updates; pre-existing cards skip them (newer state already
  // rendered below — an older update would wipe it via renderToolCard).
  prependNewTools: new Set(),
  // Index (in the current prepend batch array) where the trailing
  // agent-chunk run begins — only chunks at/after it may prefix-buffer.
  // Computed by computePrependSuffixStart before each prepend batch.
  prependSuffixStart: Infinity,
};

/**
 * Index where the current page's trailing agent-chunk run starts. Only
 * chunks in that run can be continuations of the bubble rendered just
 * below the page boundary — earlier chunks belong to older bubbles (they
 * only share mid "default" because the backend omits messageIds).
 * The scan stops at the first clearing update (user_message_chunk /
 * tool_call) walking back from the page's end.
 */
export function computePrependSuffixStart(updates) {
  let i = updates.length;
  while (i > 0) {
    const k = updates[i - 1].sessionUpdate;
    if (k === "user_message_chunk" || k === "tool_call") break;
    i--;
  }
  return i;
}

/**
 * Apply buffered prefix text accumulated during a prepend batch: each
 * existing bubble gets `buffered + existingText` and a re-render. Call once
 * after the page's handleUpdate loop, then both buffers reset.
 */
export function flushPrependBuffers() {
  for (const [mid, prefix] of renderState.prependBuf) {
    const entry = agentMsgs && agentMsgs.get(mid);
    if (entry) {
      entry.text = prefix + entry.text;
      appendAgentChunk(entry);
    }
  }
  renderState.prependBuf.clear();
  renderState.prependNewMids.clear();
  renderState.prependNewTools.clear();
  renderState.prependSuffixStart = Infinity;
  planCardInPrependBatch = false;
}

// ---- internal state ----
let addElScrollTimer = null;
let anonNodeIdCounter = 0; // unique id source for nodes without a messageId

// Track recently-sent live user messages so we can dedupe the echo that
// the ACP server may send back as a user_message_chunk update during the
// same turn. Without this, replaying user_message_chunk via userMsg()
// would render the user's message twice for live sends. Each entry is
// { text, imageFps, ts }; entries are pruned after a short window.
const recentLiveUserMsgs = [];
const LIVE_USER_MSG_DEDUP_MS = 5000;

/** Cheap fingerprint for an attached image (mime + size + data prefix). */
function imageFingerprint(content) {
  const data = content?.data || "";
  return { mimeType: content?.mimeType || "", len: data.length, head: data.slice(0, 64) };
}

// Tools whose UX is an elicitation card, not a raw JSON tool card —
// the agent's elicitation/create already renders the interactive form.
function isElicitationTool(update) {
  const n = (update.name || "").toLowerCase();
  const t = (update.title || "").toLowerCase();
  return /ask.?user.?question|elicit/.test(n) || /ask.?user.?question|elicit/.test(t);
}

// Tool kind icons
const TOOL_ICONS = {
  read: "eye", write: "edit", edit: "edit", bash: "terminal", shell: "terminal",
  search: "search", glob: "search", grep: "search", browser: "link", web: "link",
  fetch: "link", terminal: "terminal", create_file: "filePlus", delete_file: "trash",
  move_file: "rename", list: "clipboard", default: "terminal",
};

// ---- context meter state ----
// Latest context-window usage reported by the agent via usage_update. Kept
// as module state so the meter can re-render on theme/size changes without
// waiting for the next update. Reset on session/folder change.
let ctxUsed = 0;
let ctxSize = 0;
const ctxRing = $("ctx-ring");

// Per-workspace persistence: the last known { used, size } for each cwd is
// saved to localStorage so the meter can be restored on page load / server
// restart, before the next usage_update arrives. Keyed by cwd so different
// workspaces keep independent context readings.
const CTX_STATE_KEY = "devin-ctx-state";

export function initMessageRendering(deps) {
  scrollDown = deps.scrollDown;
  trimDomMessages = deps.trimDomMessages;
  debouncedCacheMessages = deps.debouncedCacheMessages;
  sendFn = deps.send;
  updateModeIndicator = deps.updateModeIndicator;
  agentMsgs = deps.agentMsgs;
  toolCards = deps.toolCards;
  getReplayingHistory = deps.getReplayingHistory;
  getReplayTotal = deps.getReplayTotal;
  getReplayReceived = deps.getReplayReceived;
  getReplayLoaderEl = deps.getReplayLoaderEl;
  setReplayLoaderEl = deps.setReplayLoaderEl;
  getLoadingHistory = deps.getLoadingHistory;
  setLoadingHistory = deps.setLoadingHistory;
  getActiveCwd = deps.getActiveCwd;
  setAvailableCommands = deps.setAvailableCommands;
  setCurrentModeId = deps.setCurrentModeId;
}

// ============================================================================
// Message rendering
// ============================================================================
// Debounced scroll for streaming — don't scroll on every single element add,
// just every 300ms at most. This makes the scroll feel gentle, not frantic.
function addEl(el) {
  const container = renderState.addElTarget || inner;
  if (renderState.addElInsertBefore && !renderState.addElTarget) {
    inner.insertBefore(el, renderState.addElInsertBefore);
  } else {
    container.appendChild(el);
  }
  if (renderState.renderingPage) renderState.pageNodes.push(el);
  // Skip trimming while prepending (scroll-back / initial replay) or staging offscreen
  if (!renderState.addElInsertBefore && !renderState.addElTarget) trimDomMessages();
  // Also skip the scroll-down timer while prepending — scroll-back restores
  // the user's position itself; a deferred scrollDown would yank them to
  // the bottom mid-scroll.
  if (addElScrollTimer || renderState.addElTarget || renderState.addElInsertBefore) return;
  addElScrollTimer = setTimeout(() => {
    addElScrollTimer = null;
    scrollDown();
  }, 300);
}
export { addEl };

function sysMsg(text, isError) {
  const el = document.createElement("div");
  el.className = "sys-msg" + (isError ? " err" : "");
  const pill = document.createElement("span");
  pill.className = "pill";
  pill.textContent = text;
  el.appendChild(pill);
  addEl(el);
}
export { sysMsg };

// ---- Paginated history replay loader ----
// Shows a non-blocking "Loading conversation history…" indicator while
// the server streams history in pages. Updates with progress so the
// user knows the app hasn't frozen during large replays.
function showReplayLoader() {
  if (!getReplayLoaderEl()) {
    const loaderEl = document.createElement("div");
    loaderEl.className = "sys-msg replay-loader";
    const pill = document.createElement("span");
    pill.className = "pill";
    loaderEl.appendChild(pill);
    addEl(loaderEl);
    setReplayLoaderEl(loaderEl);
  }
  updateReplayLoader();
}
export { showReplayLoader };

function updateReplayLoader() {
  const replayLoaderEl = getReplayLoaderEl();
  if (!replayLoaderEl) return;
  const pill = replayLoaderEl.querySelector(".pill");
  if (pill) {
    pill.textContent = getReplayTotal() > 0
      ? `Loading conversation history… (${getReplayReceived()}/${getReplayTotal()})`
      : "Loading conversation history…";
  }
}
export { updateReplayLoader };

function hideReplayLoader() {
  const replayLoaderEl = getReplayLoaderEl();
  if (replayLoaderEl) {
    replayLoaderEl.remove();
    setReplayLoaderEl(null);
  }
}
export { hideReplayLoader };

function userMsg(text, images, nodeId) {
  const row = document.createElement("div");
  row.className = "msg-row user";
  // Stable per-node id for the IndexedDB per-node cache. Live sends
  // (no nodeId) get a unique anon id so multiple sends don't collide.
  row.dataset.nodeId = nodeId || `anon-user-${anonNodeIdCounter++}`;
  const avatar = document.createElement("div");
  avatar.className = "avatar user";
  avatar.innerHTML = svgIcon("user", 15);
  const content = document.createElement("div");
  content.className = "msg-content";
  const bubble = document.createElement("div");
  bubble.className = "msg-bubble";
  if (text) bubble.textContent = text;
  if (images && images.length) {
    for (const img of images) {
      const imgEl = document.createElement("img");
      imgEl.className = "msg-image";
      imgEl.src = `data:${img.mimeType};base64,${img.data}`;
      bubble.appendChild(imgEl);
    }
  }
  content.appendChild(bubble);
  row.appendChild(avatar);
  row.appendChild(content);
  addEl(row);
  if (!getReplayingHistory() && !renderState.addElInsertBefore) scrollDown();
  return row;
}
export { userMsg };

function rememberLiveUserMsg(text, images) {
  recentLiveUserMsgs.push({
    text,
    imageFps: (images || []).map(imageFingerprint),
    ts: Date.now(),
  });
  // Prune old entries.
  const cutoff = Date.now() - LIVE_USER_MSG_DEDUP_MS;
  while (recentLiveUserMsgs.length && recentLiveUserMsgs[0].ts < cutoff) {
    recentLiveUserMsgs.shift();
  }
}
export { rememberLiveUserMsg };

function isLiveUserMsgEcho(text) {
  const cutoff = Date.now() - LIVE_USER_MSG_DEDUP_MS;
  for (const e of recentLiveUserMsgs) {
    if (e.ts < cutoff) continue;
    if (e.text === text) return true;
  }
  return false;
}

// Same dedup for attached images: sendPrompt() renders them inside the
// optimistic bubble, so the server's image user_message_chunk echo would
// show a second copy. Fingerprints are consumed on match so attaching the
// same image twice still renders both copies.
function isLiveUserImageEcho(content) {
  const fp = imageFingerprint(content);
  const cutoff = Date.now() - LIVE_USER_MSG_DEDUP_MS;
  for (const e of recentLiveUserMsgs) {
    if (e.ts < cutoff) continue;
    const i = e.imageFps.findIndex(
      (f) => f.mimeType === fp.mimeType && f.len === fp.len && f.head === fp.head,
    );
    if (i >= 0) {
      e.imageFps.splice(i, 1);
      return true;
    }
  }
  return false;
}

function ensureAgentMessage(messageId) {
  const mid = messageId || "default";
  let entry = agentMsgs.get(mid);
  if (entry) return entry;
  const row = document.createElement("div");
  row.className = "msg-row agent";
  // Stable per-node id for the IndexedDB per-node cache. The "default"
  // fallback (no messageId) is made unique so multiple such nodes don't
  // collide in the cache.
  row.dataset.nodeId = (mid === "default") ? `anon-${anonNodeIdCounter++}` : mid;
  const avatar = document.createElement("div");
  avatar.className = "avatar agent";
  avatar.innerHTML = svgIcon("agent", 16);
  const content = document.createElement("div");
  content.className = "msg-content";
  const name = document.createElement("div");
  name.className = "name";
  const label = document.createElement("span");
  label.className = "name-label";
  label.textContent = "Devin";
  name.appendChild(label);
  const activeBadge = document.createElement("span");
  activeBadge.className = "agent-active-badge hidden";
  activeBadge.innerHTML = `<span class="active-dot"></span>ACTIVE`;
  name.appendChild(activeBadge);
  const copyBtn = document.createElement("button");
  copyBtn.type = "button";
  copyBtn.className = "msg-copy";
  copyBtn.title = "Copy message";
  copyBtn.textContent = "⧉";
  name.appendChild(copyBtn);
  const bubble = document.createElement("div");
  bubble.className = "msg-bubble";
  content.appendChild(name);
  content.appendChild(bubble);
  row.appendChild(avatar);
  row.appendChild(content);
  addEl(row);
  entry = { el: bubble, text: "" };
  agentMsgs.set(mid, entry);
  // Bubbles created inside a prepend batch are new — later chunks in the
  // same batch append to them normally (they're newer than the first).
  if (renderState.addElInsertBefore) renderState.prependNewMids.add(mid);
  return entry;
}

// Turbo sentinels — kept in sync with TURBO_DONE/TURBO_BLOCKED in
// devin-session.ts. Stripped from rendered agent text; a block that is
// ONLY a sentinel collapses to a small status chip instead.
const TURBO_SENTINEL_RE = /⟪TURBO_(DONE|BLOCKED)⟫/g;
function stripTurboSentinels(text) {
  const done = text.includes("⟪TURBO_DONE⟫");
  const blocked = text.includes("⟪TURBO_BLOCKED⟫");
  return { text: text.replace(TURBO_SENTINEL_RE, ""), done, blocked };
}

function appendAgentChunk(entry) {
  entry._dirty = true;
  if (entry._raf) return;
  // Capture prepend state now — by raf time addElInsertBefore is cleared,
  // and a bottom-jump here would yank the user back down mid scroll-back.
  const suppressScroll = !!renderState.addElInsertBefore;
  entry._raf = requestAnimationFrame(() => {
    entry._raf = 0; entry._dirty = false;
    const s = stripTurboSentinels(entry.text);
    entry.el.innerHTML = s.text.trim()
      ? renderMarkdown(s.text)
      : `<span class="turbo-end-chip">${s.blocked ? "⚠ turbo: needs your input" : "✓ turbo: request complete"}</span>`;
    wireFileLinks(entry.el);
    addCopyButtons(entry.el);
    addPinButtons(entry.el);
    if (!getReplayingHistory() && !suppressScroll) scrollDown();
  });
}

function ensureToolCard(update) {
  const id = update.toolCallId;
  let card = toolCards.get(id);
  if (card) return card;
  card = document.createElement("div");
  card.className = "tool-card " + (update.status || "pending");
  card.dataset.id = id;
  card.dataset.nodeId = id; // stable per-node id for the IndexedDB cache
  const head = document.createElement("div");
  head.className = "tc-head";
  head.innerHTML = `<span class="tc-icon">${svgIcon("terminal")}</span><span class="tc-title"></span><span class="tc-status"></span><span class="tc-chevron">▶</span>`;
  const body = document.createElement("div");
  body.className = "tc-body";
  head.onclick = () => { haptic("tap"); card.classList.toggle("expanded"); };
  card.appendChild(head);
  card.appendChild(body);
  addEl(card);
  toolCards.set(id, card);
  if (renderState.addElInsertBefore) renderState.prependNewTools.add(id);
  return card;
}

function statusIcon(st) {
  if (st === "in_progress") return '<span class="st-ico spin">' + svgIcon("spinner", 11) + '</span>';
  if (st === "completed") return '<span class="st-ico">' + svgIcon("check", 11) + '</span>';
  if (st === "failed") return '<span class="st-ico">' + svgIcon("x", 11) + '</span>';
  return '<span class="st-ico">' + svgIcon("dot", 9) + '</span>';
}

function renderToolCard(card, update) {
  const head = card.querySelector(".tc-head");
  const body = card.querySelector(".tc-body");

  // Update icon based on kind/name
  if (update.kind || update.name) {
    const key = (update.kind || update.name || "").toLowerCase();
    const icon = TOOL_ICONS[key] || TOOL_ICONS.default;
    head.querySelector(".tc-icon").innerHTML = svgIcon(icon);
  }

  if (update.title) head.querySelector(".tc-title").textContent = update.title;
  if (update.status) {
    card.className = "tool-card " + update.status;
    const iconSt = update.status === "cancelled" ? "failed" : update.status;
    head.querySelector(".tc-status").innerHTML = statusIcon(iconSt) + " " + update.status;
  }

  // Only rebuild body if this update has content/locations/raw data.
  // Status-only updates (e.g. "completed") must NOT wipe the body.
  const hasNewContent = (update.content && Array.isArray(update.content) && update.content.length > 0)
    || (update.locations && update.locations.length > 0)
    || update.rawInput != null
    || update.rawOutput != null;

  if (!hasNewContent) return;

  body.innerHTML = "";

  // Show locations (file paths being accessed)
  if (update.locations && Array.isArray(update.locations) && update.locations.length) {
    const locs = document.createElement("div");
    locs.className = "tc-locations";
    for (const loc of update.locations) {
      const locEl = document.createElement("div");
      locEl.className = "tc-location";
      locEl.textContent = loc.path + (loc.line != null ? `:${loc.line}` : "");
      locs.appendChild(locEl);
    }
    body.appendChild(locs);
  }

  // Render content items. When a terminal item is present, its sibling
  // content items (command preview + captured output) are folded into a
  // single terminal block instead of loose <pre>s.
  if (update.content && Array.isArray(update.content)) {
    const hasTerminal = update.content.some((i) => i && i.type === "terminal");
    if (hasTerminal) renderTerminalBlock(body, update);
    for (const item of update.content) {
      if (!item || item.type === "terminal") continue;
      if (hasTerminal && item.type === "content") continue;
      if (item.type === "content" && item.content) {
        renderContentBlock(body, item.content);
      } else if (item.type === "diff") {
        renderDiff(body, item);
      }
    }
  }

  // Show raw input/output if available
  if (update.rawInput != null) {
    const rawIn = document.createElement("div");
    rawIn.className = "tc-raw";
    rawEl(rawIn, "Input", update.rawInput);
    body.appendChild(rawIn);
  }
  if (update.rawOutput != null) {
    const rawOut = document.createElement("div");
    rawOut.className = "tc-raw";
    rawEl(rawOut, "Output", update.rawOutput);
    body.appendChild(rawOut);
  }

  // Agent-authored display widgets (kv/table/link/progress/code/markdown/
  // actions) attached via _meta.widget on the update or its content items.
  renderMetaWidgets(body, update);
  addCopyButtons(body);
  addPinButtons(body);

  // Auto-expand if there's content
  if (body.children.length > 0 && !card.classList.contains("expanded")) {
    card.classList.add("expanded");
  }
}

// ---- bounded tool output ----------------------------------------------------
// Long command outputs are collapsed: first OUTPUT_HEAD + last OUTPUT_TAIL
// lines visible, middle hidden behind a one-click expander. Keeps 10k-line
// dumps from freezing the message list.
const OUTPUT_MAX_LINES = 80;
const OUTPUT_HEAD = 30;
const OUTPUT_TAIL = 40;

function appendCappedPre(container, text, className) {
  const lines = String(text).split("\n");
  if (lines.length <= OUTPUT_MAX_LINES) {
    const pre = document.createElement("pre");
    pre.className = className;
    pre.textContent = text;
    container.appendChild(pre);
    return;
  }
  const wrap = document.createElement("div");
  wrap.className = "tc-capped";
  const head = document.createElement("pre");
  head.className = className;
  head.textContent = lines.slice(0, OUTPUT_HEAD).join("\n");
  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "tc-expand";
  toggle.textContent = `… ${lines.length - OUTPUT_HEAD - OUTPUT_TAIL} lines hidden — show all`;
  const tail = document.createElement("pre");
  tail.className = className;
  tail.textContent = lines.slice(-OUTPUT_TAIL).join("\n");
  toggle.onclick = () => {
    const full = document.createElement("pre");
    full.className = className;
    full.textContent = text;
    wrap.replaceChildren(full);
  };
  wrap.appendChild(head);
  wrap.appendChild(toggle);
  wrap.appendChild(tail);
  container.appendChild(wrap);
}

/**
 * Render a terminal content item as a real terminal block: "$ command"
 * header, captured output, exit-code footer — instead of the old
 * `[Terminal: <id>]` placeholder. The output text comes from the sibling
 * `content` items on the same update (the final tool_call_update embeds
 * the command's stdout as text).
 */
function renderTerminalBlock(container, update) {
  const wrap = document.createElement("div");
  wrap.className = "tc-terminal-block";

  // Command: rawInput.command, else the tool://preview shell-script
  // resource the tool_call carried.
  let cmd = null;
  const ri = update.rawInput;
  if (ri && typeof ri === "object") cmd = ri.command ?? ri.cmd ?? null;
  if (!cmd && Array.isArray(update.content)) {
    for (const it of update.content) {
      const c = it?.content;
      if (it?.type === "content" && c?.type === "resource"
          && c.resource?.uri === "tool://preview" && c.resource?.text) {
        cmd = c.resource.text;
        break;
      }
    }
  }
  const head = document.createElement("div");
  head.className = "tc-terminal-head";
  head.textContent = cmd ? "$ " + cmd : "terminal";
  wrap.appendChild(head);

  // Output: text content items on this update hold the captured stdout.
  let output = "";
  if (Array.isArray(update.content)) {
    for (const it of update.content) {
      const c = it?.content;
      if (it?.type === "content" && c?.type === "text" && c.text) {
        output += (output ? "\n" : "") + c.text;
      }
    }
  }
  if (output) {
    appendCappedPre(wrap, output, "tc-terminal-output");
  } else {
    const pre = document.createElement("pre");
    pre.className = "tc-terminal-output empty";
    pre.textContent = "(no captured output)";
    wrap.appendChild(pre);
  }

  // Exit-code footer (Devin CLI reports it via _meta.terminal_exit).
  const exit = update._meta?.terminal_exit;
  if (exit && exit.exit_code != null) {
    const foot = document.createElement("div");
    foot.className = "tc-terminal-exit" + (exit.exit_code === 0 ? " ok" : " err");
    foot.textContent = "exit " + exit.exit_code;
    wrap.appendChild(foot);
  }
  container.appendChild(wrap);
}

function rawEl(container, label, raw) {
  const lbl = document.createElement("div");
  lbl.className = "tc-raw-label";
  lbl.textContent = label;
  container.appendChild(lbl);
  const pre = document.createElement("pre");
  pre.textContent = typeof raw === "string" ? raw : JSON.stringify(raw, null, 2);
  container.appendChild(pre);
}

function renderContentBlock(container, content) {
  if (!content || !content.type) return;
  switch (content.type) {
    case "text": {
      appendCappedPre(container, content.text || "", "tc-text");
      break;
    }
    case "image": {
      if (content.data && content.mimeType) {
        const img = document.createElement("img");
        img.className = "tc-image";
        img.src = `data:${content.mimeType};base64,${content.data}`;
        img.alt = content.uri || "Tool output image";
        container.appendChild(img);
      }
      break;
    }
    case "resource_link": {
      const link = document.createElement("div");
      link.className = "tc-resource-link";
      link.innerHTML = svgIcon("link", 12) + " " + (content.uri || content.name || "resource").replace(/</g,"&lt;");
      container.appendChild(link);
      break;
    }
    case "resource": {
      if (content.resource?.text) {
        appendCappedPre(container, content.resource.text, "tc-text");
      }
      break;
    }
    default: {
      const pre = document.createElement("pre");
      pre.className = "tc-text";
      pre.textContent = JSON.stringify(content, null, 2);
      container.appendChild(pre);
    }
  }
}

function renderDiff(container, diff) {
  const wrapper = document.createElement("div");
  wrapper.className = "tc-diff";

  // File path header
  const pathEl = document.createElement("div");
  pathEl.className = "diff-path";
  pathEl.textContent = diff.path || "(unknown file)";
  wrapper.appendChild(pathEl);

  // Generate a unified diff view
  const oldLines = (diff.oldText || "").split("\n");
  const newLines = (diff.newText || "").split("\n");

  // Simple line-by-line diff
  const diffLines = computeSimpleDiff(oldLines, newLines);
  const pre = document.createElement("pre");
  pre.className = "diff-content";
  for (const dl of diffLines) {
    const span = document.createElement("span");
    span.className = "diff-line " + dl.type;
    span.textContent = dl.text + "\n";
    pre.appendChild(span);
  }
  wrapper.appendChild(pre);
  container.appendChild(wrapper);
}

function computeSimpleDiff(oldLines, newLines) {
  // Use LCS-based diff for reasonable results
  const m = oldLines.length, n = newLines.length;
  // For very large diffs, fall back to showing all new lines as additions
  if (m > 5000 || n > 5000) {
    return newLines.map((text) => ({ type: "add", text }));
  }
  // Build LCS table
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (oldLines[i] === newLines[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  // Backtrack to produce diff
  const result = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (oldLines[i] === newLines[j]) {
      result.push({ type: "ctx", text: "  " + oldLines[i] });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      result.push({ type: "del", text: "- " + oldLines[i] });
      i++;
    } else {
      result.push({ type: "add", text: "+ " + newLines[j] });
      j++;
    }
  }
  while (i < m) { result.push({ type: "del", text: "- " + oldLines[i++] }); }
  while (j < n) { result.push({ type: "add", text: "+ " + newLines[j++] }); }
  return result;
}
// Each plan update carries the full entries list, so a single card is
// re-rendered in place — stacking a new card per update left stale
// snapshots behind whose in_progress spinners animated forever, even
// after a later update marked the entry completed.
let planCardEl = null;
// Set when the card is created inside a scroll-back prepend batch, so
// updates within that same batch still apply to it (see prependNewTools).
let planCardInPrependBatch = false;

function renderPlan(update) {
  if (renderState.addElInsertBefore && planCardEl && planCardEl.parentNode && !planCardInPrependBatch) {
    // Older page prepending above a card that already shows newer state —
    // re-rendering this update would wipe it.
    return;
  }
  if (!planCardEl || !planCardEl.parentNode) {
    planCardEl = document.createElement("div");
    planCardEl.className = "plan-card";
    addEl(planCardEl);
    if (renderState.addElInsertBefore) planCardInPrependBatch = true;
  }
  planCardEl.innerHTML = "";
  const head = document.createElement("div");
  head.className = "pl-head";
  const entries = Array.isArray(update.entries) ? update.entries : [];
  const done = entries.filter(e => e.status === "completed").length;
  head.innerHTML = `<span class="icon pl-ico">${svgIcon("plan", 14)}</span><span class="title">Plan</span>${entries.length ? `<span class="pl-count">${done}/${entries.length}</span>` : ""}`;
  planCardEl.appendChild(head);
  if (entries.length) {
    const ul = document.createElement("ul");
    for (const e of entries) {
      const li = document.createElement("li");
      li.className = (e.priority || "") + " " + (e.status || "");
      const st = document.createElement("span");
      st.className = "pl-status";
      st.innerHTML = e.status === "completed" ? svgIcon("checkcircle", 13)
                   : e.status === "in_progress" ? `<span class="st-ico spin">${svgIcon("spinner", 12)}</span>`
                   : svgIcon("circle", 12);
      const span = document.createElement("span");
      span.className = "pl-text";
      span.textContent = e.content || "";
      li.appendChild(st);
      li.appendChild(span);
      if (e.priority) {
        const p = document.createElement("span");
        p.className = "p";
        p.textContent = e.priority.toUpperCase();
        li.appendChild(p);
      }
      ul.appendChild(li);
    }
    planCardEl.appendChild(ul);
  }
}

function loadCtxStateMap() {
  try { return JSON.parse(localStorage.getItem(CTX_STATE_KEY) || "{}"); }
  catch { return {}; }
}
function saveCtxState(cwd) {
  if (!cwd || !ctxSize) return;
  const map = loadCtxStateMap();
  map[cwd] = { used: ctxUsed, size: ctxSize, ts: Date.now() };
  try { localStorage.setItem(CTX_STATE_KEY, JSON.stringify(map)); }
  catch { /* quota — ignore */ }
}
function restoreCtxState(cwd) {
  if (!cwd) return;
  const saved = loadCtxStateMap()[cwd];
  if (!saved || !saved.size) return;
  ctxUsed = saved.used || 0;
  ctxSize = saved.size;
  renderContextMeter();
}
export { restoreCtxState };

/** Format a token count compactly (e.g. 124000 → "124k", 1200000 → "1.2M"). */
function fmtTokens(n) {
  if (n >= 1e6) return (n / 1e6).toFixed(n >= 1e7 ? 0 : 1) + "M";
  if (n >= 1e3) return Math.round(n / 1e3) + "k";
  return String(n);
}

/**
 * Render the circular context meter around the Send button. The ring is a
 * conic-gradient that fills clockwise from 0deg to pct*360deg. Color shifts
 * by pressure tier (blue → yellow → red) so "running low" is visible without
 * reading numbers. Hover the ring or button for the exact percentage and
 * token counts via the title attribute.
 */
function renderContextMeter() {
  if (!ctxRing) return;
  const pct = ctxSize > 0 ? Math.min(ctxUsed / ctxSize, 1) : 0;
  const deg = Math.round(pct * 360);
  const color = pct >= 0.9 ? "var(--red)" : pct >= 0.7 ? "var(--yellow)" : "var(--accent)";
  // Empty (0%) renders as a full track — no sliver of accent.
  ctxRing.style.background = deg > 0
    ? `conic-gradient(${color} 0deg ${deg}deg, var(--border) ${deg}deg 360deg)`
    : "var(--border)";
  const title = ctxSize > 0
    ? `${Math.round(pct * 100)}% · ${fmtTokens(ctxUsed)} / ${fmtTokens(ctxSize)} tokens`
    : "Send";
  sendBtn.title = title;
  ctxRing.title = title;
}

/** Reset the context meter to its empty/unknown state (call on session change). */
function resetContextMeter() {
  ctxUsed = 0;
  ctxSize = 0;
  renderContextMeter();
}
export { resetContextMeter };

function renderUsage(update) {
  if (update.used != null) ctxUsed = update.used;
  if (update.size != null) ctxSize = update.size;
  renderContextMeter();
  saveCtxState(getActiveCwd());
}

/**
 * Replayed elicitation journal rows (sessionUpdate:"elicitation") — render
 * a compact read-only card showing what the agent asked and how it
 * resolved. Live interactive cards come from elicitation_request frames
 * (elicitation.js); this is the history view for late joiners/scroll-back.
 */
function renderElicitationReplay(update) {
  const card = document.createElement("div");
  card.className = "elicit-card elicit-replay";
  card.dataset.nodeId = `elicit-${update.elicitationId || anonNodeIdCounter++}`;
  if (update.elicitationId) card.dataset.elicitationId = update.elicitationId;
  const head = document.createElement("div");
  head.className = "pm-head";
  const icon = svgIcon(update.mode === "url" ? "link" : "question");
  head.innerHTML = `<span class="pm-icon">${icon}</span><span class="pm-title"></span><span class="el-state"></span>`;
  head.querySelector(".pm-title").textContent = update.message || "Input requested";
  const stateEl = head.querySelector(".el-state");
  const state = update.state || "pending";
  stateEl.textContent = state === "answered" ? "answered" : state === "declined" ? "skipped" : state === "cancelled" ? "cancelled" : "unanswered";
  stateEl.classList.add("el-" + (state === "answered" ? "answered" : state === "pending" ? "pending" : "closed"));
  card.appendChild(head);
  if (update.url) {
    const a = document.createElement("a");
    a.className = "el-url";
    a.href = update.url;
    a.target = "_blank";
    a.rel = "noopener";
    a.textContent = update.url;
    card.appendChild(a);
  }
  if (update.answerSummary && typeof update.answerSummary === "object") {
    const sum = document.createElement("div");
    sum.className = "el-answer";
    if (update.answerSummary.redacted) {
      sum.textContent = "answer redacted";
    } else {
      const parts = Object.entries(update.answerSummary)
        .map(([k, v]) => `${k}: ${v}`)
        .join(" · ");
      sum.textContent = parts || "answered";
    }
    card.appendChild(sum);
  }
  addEl(card);
}
function renderPermission(msg) {
  const card = document.createElement("div");
  card.className = "perm-card";
  card.dataset.id = msg.id;

  // Header with icon + tool title
  const head = document.createElement("div");
  head.className = "pm-head";
  const picon = svgIcon(TOOL_ICONS[(msg.toolCall.kind || "").toLowerCase()] || "shield");
  head.innerHTML = `<span class="pm-icon">${picon}</span><span class="pm-title"></span>`;
  head.querySelector(".pm-title").textContent = msg.toolCall.title;
  card.appendChild(head);

  // Tool kind badge
  if (msg.toolCall.kind) {
    const kindBadge = document.createElement("div");
    kindBadge.className = "pm-kind";
    kindBadge.textContent = msg.toolCall.kind;
    card.appendChild(kindBadge);
  }

  // Show file locations
  if (msg.toolCall.locations && msg.toolCall.locations.length) {
    const locs = document.createElement("div");
    locs.className = "pm-locations";
    for (const loc of msg.toolCall.locations) {
      const locEl = document.createElement("div");
      locEl.className = "pm-location";
      locEl.textContent = "📁 " + loc.path + (loc.line != null ? `:${loc.line}` : "");
      locs.appendChild(locEl);
    }
    card.appendChild(locs);
  }

  // Show content (diffs, text, etc.)
  if (msg.toolCall.content && Array.isArray(msg.toolCall.content)) {
    const body = document.createElement("div");
    body.className = "pm-content";
    for (const item of msg.toolCall.content) {
      if (!item) continue;
      if (item.type === "content" && item.content) {
        renderContentBlock(body, item.content);
      } else if (item.type === "diff") {
        renderDiff(body, item);
      }
    }
    if (body.children.length) card.appendChild(body);
  }

  // Show raw input if available
  if (msg.toolCall.rawInput != null) {
    const raw = document.createElement("div");
    raw.className = "pm-raw";
    rawEl(raw, "Input", msg.toolCall.rawInput);
    card.appendChild(raw);
  }

  // Action buttons — simplified to Allow / Allow always / Reject
  const opts = document.createElement("div");
  opts.className = "pm-actions";

  // Find the allow options from the ACP options array
  const allowOnce = msg.options.find((o) => o.kind === "allow_once");
  const allowAlways = msg.options.find((o) => o.kind === "allow_always");
  const rejectOnce = msg.options.find((o) => o.kind === "reject_once");
  const rejectAlways = msg.options.find((o) => o.kind === "reject_always");
  // Fallbacks: use first option as allow, any option with "allow" in name
  const allowOpt = allowOnce || allowAlways || msg.options.find((o) => /allow/i.test(o.name)) || msg.options[0];
  const bypassOpt = allowAlways || msg.options.find((o) => o.kind === "allow_always" || /always|bypass/i.test(o.name));
  const rejectOpt = rejectOnce || rejectAlways || msg.options.find((o) => /reject/i.test(o.name));

  function resolve(optionId) {
    sendFn({ type: "permission_response", id: msg.id, optionId });
    card.classList.add("resolved");
    [...opts.children].forEach((c) => c.disabled = true);
  }
  function reject() {
    if (rejectOpt) {
      sendFn({ type: "permission_response", id: msg.id, optionId: rejectOpt.optionId });
    } else {
      sendFn({ type: "permission_cancel", id: msg.id });
    }
    card.classList.add("rejected");
    [...opts.children].forEach((c) => c.disabled = true);
  }

  if (allowOpt) {
    const allowBtn = document.createElement("button");
    allowBtn.className = "pm-btn allow";
    allowBtn.textContent = "✓ Allow";
    allowBtn.onclick = () => { haptic("success"); resolve(allowOpt.optionId); };
    opts.appendChild(allowBtn);
  }
  if (bypassOpt && bypassOpt !== allowOpt) {
    const bypassBtn = document.createElement("button");
    bypassBtn.className = "pm-btn bypass";
    bypassBtn.textContent = "✓ Allow always";
    bypassBtn.title = "Allow this tool kind without asking again";
    bypassBtn.onclick = () => { haptic("success"); resolve(bypassOpt.optionId); };
    opts.appendChild(bypassBtn);
  }
  const rejectBtn = document.createElement("button");
  rejectBtn.className = "pm-btn reject";
  rejectBtn.textContent = "✕ Reject";
  rejectBtn.onclick = () => { haptic("error"); reject(); };
  opts.appendChild(rejectBtn);

  card.appendChild(opts);
  addEl(card);
  scrollDown();
}
export { renderPermission };

function dismissPermissionCard(id, outcome) {
  for (const card of document.querySelectorAll(".perm-card")) {
    if (card.dataset.id !== id) continue;
    card.classList.add(outcome === "cancelled" ? "rejected" : "resolved");
    const opts = card.querySelector(".pm-actions");
    if (opts) [...opts.children].forEach((c) => c.disabled = true);
  }
}
export { dismissPermissionCard };

function handleUpdate(update, idx) {
  // If we're loading history (session resume/switch), clear cached content
  // on the first update so the replayed history replaces it cleanly, unless
  // an atomic swap is staged.
  if (getLoadingHistory() && !renderState.replaySwapPending) {
    inner.innerHTML = "";
    agentMsgs.clear();
    toolCards.clear();
    setLoadingHistory(false);
  }
  switch (update.sessionUpdate) {
    case "agent_message_chunk": {
      // Hide typing indicator once Devin starts actually responding
      typingIndicator.classList.add("hidden");
      const mid = update.messageId || "default";
      // Prepend batch: chunks in the trailing run whose bubble sits right
      // below the page boundary are EARLIER text of that bubble — buffer
      // as a prefix instead of appending (appending reads tail-then-head).
      // The boundary-node check matters for mid "default": every bubble
      // shares it, so same-mid alone can't prove same-bubble.
      if (renderState.addElInsertBefore && !renderState.prependNewMids.has(mid)) {
        const entry0 = agentMsgs.get(mid);
        if (
          entry0 &&
          idx != null && idx >= renderState.prependSuffixStart &&
          entry0.el.closest(".msg-row") === renderState.addElInsertBefore
        ) {
          if (update.content?.type === "text" && update.content.text) {
            renderState.prependBuf.set(mid, (renderState.prependBuf.get(mid) || "") + update.content.text);
          }
          break;
        }
        // The registered entry is a different, newer bubble below the
        // boundary — drop the map entry so this chunk opens a fresh
        // bubble at the top instead of appending into the wrong one.
        if (entry0) agentMsgs.delete(mid);
      }
      const entry = ensureAgentMessage(mid);
      // Agent-authored display widgets on the chunk itself — rendered as
      // their own card node below the bubble.
      if (update._meta?.widget) {
        const w = document.createElement("div");
        w.dataset.nodeId = `widget-${update.messageId || anonNodeIdCounter++}`;
        addEl(w);
        renderMetaWidgets(w, update);
      }
      if (update.content?.type === "text") {
        entry.text += update.content.text;
        appendAgentChunk(entry);
      } else if (update.content?.type === "image" && update.content.data) {
        // Append image to the agent message bubble
        const img = document.createElement("img");
        img.className = "msg-image";
        img.src = `data:${update.content.mimeType};base64,${update.content.data}`;
        img.alt = update.content.uri || "Agent image";
        entry.el.appendChild(img);
        if (!renderState.addElInsertBefore) scrollDown();
      }
      break;
    }
    case "user_message_chunk": {
      // Clear agent message map so the next agent response creates a fresh
      // bubble after the user's message, instead of appending to a prior one.
      agentMsgs.clear();
      // Render user messages via the same userMsg() path used for live
      // sends, so they appear on the right with the "You" avatar and
      // proper styling (not italicized inside an agent bubble). During a
      // live turn the client already rendered the message optimistically
      // in sendPrompt(); the ACP server may echo it back as a
      // user_message_chunk — dedupe against recently-sent live messages
      // to avoid rendering it twice.
      if (update.content?.type === "text") {
        // Turbo check prompts are gateway-injected — render as a muted
        // chip instead of a user bubble (journaled with `turbo: true`).
        if (update.turbo) { sysMsg("⚡ turbo — verifying the request is complete…", false); break; }
        if (isLiveUserMsgEcho(update.content.text)) break;
        userMsg(update.content.text, [], update.messageId);
      } else if (update.content?.type === "image" && update.content.data) {
        if (isLiveUserImageEcho(update.content)) break;
        userMsg("", [{ mimeType: update.content.mimeType, data: update.content.data }], update.messageId);
      }
      break;
    }
    case "tool_call": {
      // Clear agent message map so the next agent_message_chunk creates a
      // new bubble. This produces the correct interleaved rendering:
      // text bubble → tool card → text bubble → tool card → ...
      // Without this, all text piles into one bubble at the top while
      // tool cards accumulate below it.
      agentMsgs.clear();
      // Prepend batch + pre-existing card: the card already shows newer
      // state — re-rendering this older update would wipe it.
      if (renderState.addElInsertBefore && toolCards.has(update.toolCallId) && !renderState.prependNewTools.has(update.toolCallId)) {
        break;
      }
      if (isElicitationTool(update)) break; // elicitation card covers it
      renderToolCard(ensureToolCard(update), update);
      break;
    }
    case "tool_call_update": {
      if (renderState.addElInsertBefore && toolCards.has(update.toolCallId) && !renderState.prependNewTools.has(update.toolCallId)) {
        break;
      }
      if (isElicitationTool(update)) break;
      renderToolCard(toolCards.get(update.toolCallId) || ensureToolCard(update), update);
      break;
    }
    case "plan": renderPlan(update); break;
    case "elicitation": renderElicitationReplay(update); break;
    case "usage_update": renderUsage(update); break;
    case "agent_thought_chunk":
      break;
    case "available_commands_update":
      setAvailableCommands(update.availableCommands || []);
      break;
    case "session_info_update":
    case "config_option_update":
      break;
    case "current_mode_update":
      setCurrentModeId(update.currentModeId || null);
      updateModeIndicator();
      break;
    default:
      break;
  }
  // Cache during streaming (debounced) so disconnects don't lose content
  if (!getLoadingHistory()) debouncedCacheMessages();
}
export { handleUpdate };
